import React, { useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { Alert, Badge, Button, Col, Container, FormGroup, Row, Spinner } from 'reactstrap';
import { VocabularyService } from '../../../services';
import useDebounce from '../../../utils/useDebounce';
import fileDownload from 'js-file-download';
import moment from 'moment';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'learning', label: 'Learning' },
  { value: 'memorized', label: 'Memorized' },
];

const playAudio = (url) => {
  new Audio(url).play().catch(() => {});
};

const wordOf = (item) => item.lexical_entry?.display_word || item.lexical_entry?.lemma || '';
const firstSense = (item) => item.lexical_entry?.senses?.[0] || {};

const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const PageVocabularies = () => {
  const [keyword, setKeyword] = useState('');
  const [filter, setFilter] = useState('learning');
  const [error, setError] = useState(null);

  const debouncedKeyword = useDebounce(keyword.trim().toLowerCase(), 300);

  const { data, isLoading, isError, error: loadError, refetch } = useQuery(
    'getVocabularies',
    () => VocabularyService.getVocabularies(),
    { retry: false },
  );

  const allItems = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const filtered = useMemo(
    () =>
      allItems.filter((item) => {
        if (filter === 'learning' && item.memorized_flg) return false;
        if (filter === 'memorized' && !item.memorized_flg) return false;
        if (!debouncedKeyword) return true;
        return (
          wordOf(item).toLowerCase().includes(debouncedKeyword) ||
          String(item.translation || '').toLowerCase().includes(debouncedKeyword)
        );
      }),
    [allItems, filter, debouncedKeyword],
  );

  const onMutationError = (err) => setError(VocabularyService.getErrorMessage(err));
  const onMutationSuccess = () => {
    setError(null);
    refetch();
  };

  const { mutate: deleteWord } = useMutation(VocabularyService.deleteVocabulary, {
    onSuccess: onMutationSuccess,
    onError: onMutationError,
  });

  const { mutate: toggleMemorized } = useMutation(VocabularyService.updateVocabulary, {
    onSuccess: onMutationSuccess,
    onError: onMutationError,
  });

  const exportCsv = () => {
    const rows = [['No', 'Word', 'Translation', 'Pronunciation', 'Definition', 'Example', 'Memorized', 'Created at']];
    allItems.forEach((item, i) => {
      const sense = firstSense(item);
      rows.push([
        i + 1,
        wordOf(item),
        item.translation,
        item.lexical_entry?.pronunciation,
        sense.definition,
        sense.example,
        item.memorized_flg ? 'yes' : 'no',
        moment(item.created_at).format('YYYY-MM-DD'),
      ]);
    });
    const csv = rows.map((row) => row.map(csvCell).join(',')).join('\n');
    fileDownload(`﻿${csv}`, `Vocabularies_${moment().format('YYYY_MM_DD')}.csv`);
  };

  const handleDelete = (item) => {
    if (window.confirm(`Delete "${wordOf(item)}" from your words?`)) deleteWord(item.id);
  };

  return (
    <section className="section p-0 mt-4 mb-5 pb-5">
      <Container>
        <Row className="align-items-center mb-3">
          <Col md={7}>
            <FormGroup className="mb-0">
              <input
                type="text"
                className="form-control rounded-pill shadow-sm"
                placeholder="Search your words..."
                value={keyword}
                maxLength={100}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </FormGroup>
          </Col>
          <Col md={5} className="text-md-end mt-2 mt-md-0">
            <Button
              color="outline-primary"
              size="sm"
              className="rounded-pill"
              onClick={exportCsv}
              disabled={allItems.length === 0}
            >
              <i className="uil uil-file-download me-1" />
              Export CSV
            </Button>
          </Col>
        </Row>

        <div className="d-flex gap-2 flex-wrap mb-3">
          {FILTERS.map((f) => (
            <Button
              key={f.value}
              size="sm"
              color={filter === f.value ? 'primary' : 'outline-secondary'}
              className="rounded-pill"
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
          <span className="text-muted small align-self-center ms-auto">
            {filtered.length} of {allItems.length} words
          </span>
        </div>

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>
        )}

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-2">Loading your words...</p>
          </div>
        ) : isError ? (
          <Alert color="danger">
            {VocabularyService.getErrorMessage(loadError)}{' '}
            <Button color="link" className="p-0 align-baseline" onClick={() => refetch()}>Retry</Button>
          </Alert>
        ) : filtered.length === 0 ? (
          <Alert color="info">
            {allItems.length > 0
              ? 'No words match your search or filter.'
              : "You haven't saved any words yet. Use the extension to add words while you browse."}
          </Alert>
        ) : (
          <>
            <div className="bg-white rounded shadow-sm">
              {filtered.map((item, i) => {
                const word = wordOf(item);
                const sense = firstSense(item);
                const { pronunciation, audio_url: audioUrl } = item.lexical_entry || {};
                return (
                  <div
                    key={item.id}
                    className="d-flex align-items-start gap-3 p-3"
                    style={{ borderBottom: i < filtered.length - 1 ? '1px solid #f1f3f5' : 'none' }}
                  >
                    <div className="flex-grow-1 min-width-0">
                      <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                        <span className="fw-bold text-dark">
                          {word.charAt(0).toUpperCase() + word.slice(1)}
                        </span>
                        {audioUrl && (
                          <button
                            className="btn btn-link p-0 text-muted"
                            style={{ fontSize: '1rem', lineHeight: 1 }}
                            onClick={() => playAudio(audioUrl)}
                            title="Listen"
                          >
                            <i className="uil uil-volume-up" />
                          </button>
                        )}
                        {pronunciation && <span className="text-muted small">{pronunciation}</span>}
                        {item.translation && (
                          <Badge color="light" className="text-primary border fw-normal">
                            {item.translation}
                          </Badge>
                        )}
                        {item.memorized_flg && (
                          <Badge color="success" pill className="fw-normal">Memorized</Badge>
                        )}
                      </div>
                      {sense.definition && (
                        <p className="mb-0 text-dark small">{sense.definition}</p>
                      )}
                      {sense.example && (
                        <p className="mb-0 text-muted small fst-italic">"{sense.example}"</p>
                      )}
                    </div>
                    <div className="d-flex flex-column gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        color={item.memorized_flg ? 'success' : 'outline-secondary'}
                        className="rounded-pill"
                        style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                        onClick={() => toggleMemorized({ id: item.id, memorized_flg: !item.memorized_flg })}
                        title={item.memorized_flg ? 'Mark as learning' : 'Mark as memorized'}
                      >
                        {item.memorized_flg ? '✓ Memorized' : 'Mark Memorized'}
                      </Button>
                      <Button
                        size="sm"
                        color="outline-danger"
                        className="rounded-pill"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => handleDelete(item)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </Container>
    </section>
  );
};

export default PageVocabularies;
